import { useMemo, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { useIcdSearch } from "@/hooks/useIcdSearch";
import { fonts } from "@/lib/theme";

interface IcdCode {
    code: string;
    description: string;
}

interface Props {
    suggestedCodes: IcdCode[];
    selectedCodes: Set<string>;
    onToggle: (code: string) => void;
    onAdd: (code: IcdCode) => void;
    existingDiagnoses?: string[];
}

export function IcdChipPicker({
    suggestedCodes,
    selectedCodes,
    onToggle,
    onAdd,
    existingDiagnoses: _existingDiagnoses = []
}: Props) {
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const excludeCodes = useMemo(
        () => new Set(suggestedCodes.map((s) => s.code)),
        [suggestedCodes]
    );
    const { results: searchResults, loading: searching } = useIcdSearch(searchQuery, excludeCodes);

    const handleAddFromSearch = (code: IcdCode) => {
        onAdd(code);
        setSearchQuery("");
        setShowSearch(false);
    };

    return (
        <View>
            {/* Suggested chips — same pill style as frequency/notes */}
            {suggestedCodes.length > 0 && (
                <View style={styles.chipsWrap}>
                    {suggestedCodes.map((icd) => {
                        const selected = selectedCodes.has(icd.code);
                        return (
                            <TouchableOpacity
                                key={icd.code}
                                style={[styles.chip, selected && styles.chipSelected]}
                                onPress={() => onToggle(icd.code)}
                            >
                                <Text
                                    style={[styles.chipText, selected && styles.chipTextSelected]}
                                    numberOfLines={1}
                                >
                                    {selected ? "\u2713 " : ""}
                                    {icd.code} {icd.description}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>
            )}

            {/* "Other" chip — toggles search input */}
            <View style={styles.chipsWrap}>
                <Pressable
                    onPress={() => setShowSearch(!showSearch)}
                    style={[styles.otherChip, showSearch && styles.otherChipActive]}
                >
                    <Text style={[styles.otherChipText, showSearch && styles.otherChipTextActive]}>
                        Other
                    </Text>
                </Pressable>
            </View>

            {/* Search — shown when "Other" is tapped */}
            {showSearch && (
                <View style={styles.searchSection}>
                    <TextInput
                        style={styles.searchInput}
                        placeholder="Search by code or description..."
                        placeholderTextColor="#B0AECE"
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        autoCapitalize="characters"
                        autoCorrect={false}
                        spellCheck={false}
                        autoFocus
                    />
                    {searchResults.length > 0 && (
                        <View style={styles.searchResults}>
                            {searchResults.map((r) => (
                                <TouchableOpacity
                                    key={r.code}
                                    style={styles.searchResultItem}
                                    onPress={() => handleAddFromSearch(r)}
                                >
                                    <Text style={styles.searchResultCode}>{r.code}</Text>
                                    <Text style={styles.searchResultDesc} numberOfLines={1}>
                                        {r.description}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    )}
                    {searching && (
                        <View style={styles.searchStatusRow}>
                            <ActivityIndicator size="small" color="#7C3AED" />
                            <Text style={styles.searchStatusText}>Searching full ICD library…</Text>
                        </View>
                    )}
                    {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                        <Text style={styles.searchEmptyText}>No matching diagnosis codes</Text>
                    )}
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    chipsWrap: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 5,
        marginBottom: 8
    },
    chip: {
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: "#EDEAF2",
        backgroundColor: "#FFFFFF"
    },
    chipSelected: {
        borderColor: "#45D2B0",
        backgroundColor: "#45D2B0"
    },
    chipText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "#4A4A68"
    },
    chipTextSelected: {
        color: "#FFFFFF"
    },
    otherChip: {
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderStyle: "dashed" as "solid",
        borderColor: "#DDD8E6",
        backgroundColor: "#FFFFFF"
    },
    otherChipActive: {
        borderColor: "#E748F5",
        backgroundColor: "#E748F5",
        borderStyle: "solid" as const
    },
    otherChipText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "#8B89A8"
    },
    otherChipTextActive: {
        color: "#FFFFFF"
    },
    searchSection: {
        marginTop: 8
    },
    searchInput: {
        borderWidth: 1.5,
        borderColor: "#EDEAF2",
        borderRadius: 10,
        padding: 10,
        fontSize: 13,
        color: "#1F1547",
        backgroundColor: "#FFFFFF"
    },
    searchResults: {
        borderWidth: 1,
        borderColor: "#EDEAF2",
        borderRadius: 10,
        marginTop: 6,
        overflow: "hidden"
    },
    searchResultItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        padding: 14,
        borderBottomWidth: 1,
        borderBottomColor: "#FAFAF9"
    },
    searchResultCode: {
        fontSize: 14,
        fontWeight: "700",
        color: "#7C3AED"
    },
    searchResultDesc: {
        fontSize: 13,
        color: "#6B6B8D",
        flex: 1
    },
    searchStatusRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 8,
        paddingHorizontal: 2
    },
    searchStatusText: {
        fontSize: 12,
        color: "#8B89A8",
        fontFamily: fonts.semiBold
    },
    searchEmptyText: {
        fontSize: 12,
        color: "#8B89A8",
        fontFamily: fonts.semiBold,
        marginTop: 8,
        paddingHorizontal: 2
    }
});
